import { ButtonBrand } from 'components/ButtonBrand';
import { Row } from 'components/Row';
import { Body, Eyebrow, H4, UtilityText } from 'components/Typography';
import {
  ConfirmationBackgroundContainer,
  ConfirmationButtonRow,
  ConfirmationCardContainer,
  ConfirmationCardContent,
  ConfirmationContainer,
  ConfirmationContainerLeft,
  ConfirmationContainerRight,
  ConfirmationImage,
  ConfirmationPaymentDisplay,
  DonateAgainJumpButton,
  // IFrame,
  // IFrameContainer,
  ProcessingCardContent,
  ProcessingImage,
} from './styled';
import theme from 'config/theme';
import Spacing from 'components/Spacing';
import {
  capitalizeString,
  formatNumber,
  formatNumberAsCurrency,
  getUrlParams,
  scrollToTop,
} from 'helpers/utils';
import { FC, useEffect, useRef } from 'react';
import { useAppSelector } from 'helpers/hooks';
import { getPayData } from 'selectors/pay';
import { getDonatePageData } from 'selectors/donatePage';
import { getEventData } from 'selectors/eventPage';
import { PayResponseData } from 'store/pay/types';
import { PopupModal } from 'components/PopupModal';
import {
  getDonationTotalFromPayResponse,
  getIsCheckoutMonthly,
  getTipFromPayResponse,
} from './Form/utils';
import { trackDECheckoutConfirmationViewed } from 'helpers/mixpanelEvents';
import { useCompleteSavedCart } from 'hooks/useCompleteSavedCart';
import { useShareUrl } from 'hooks/useShareUrl';
import { getUserState } from 'selectors/user';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { ProfileCard } from 'components/Cards/ProfileCard';
import { trackPixel, trackPurchaseOnce } from 'helpers/pixel';
import {
  getActionNoun,
  getActionVerb,
  getIsStatesProjectRedirectPage,
  STATES_PROJECT_REDIRECT,
} from 'helpers/donate';
import Column from 'components/Column';
import { DEDonateAgain } from './DEDonateAgain';
import { ENV_CONFIG } from 'config/environment';

const DONATE_AGAIN_SECTION_ID = 'donate-again-section';
const SHOW_DONATE_AGAIN = false;

const getPaymentMethodDisplay = (payment: PayResponseData) => {
  const {
    payment_method: paymentMethod,
    cc_brand: ccBrand,
    cc_last_four: ccLastFour,
    ach_account_type: achType,
  } = payment;

  // TODO: verify last_4 for ach
  if (paymentMethod === 'credit_card') {
    return `${ccBrand} ${ccLastFour || '****'}`;
  }
  return `ACH ${achType} ****`;
};

const getIsDonationPending = (payment: PayResponseData) => {
  const { line_items: lineItems } = payment;
  return lineItems.some(
    item =>
      item.status === 'exception_during_authorization' ||
      item.status === 'authorization_awaiting_manual_review'
  );
};

interface Props {
  candidateCount: number;
  candidates: CandidateType[];
  clientId?: number;
  donationAmount: number;
  handleNewDonationSelections: () => void;
  isEvent: boolean;
  pageImage?: string;
  postDonateMsg?: string;
}

export const DEConfirmation: FC<Props> = ({
  candidateCount,
  candidates,
  clientId,
  donationAmount,
  handleNewDonationSelections,
  isEvent,
  pageImage,
  postDonateMsg,
}) => {
  const { urlPartner } = getUrlParams();
  const { payment, userState, page } = useAppSelector(state => ({
    payment: getPayData(state),
    userState: getUserState(state),
    page: isEvent ? getEventData(state) : getDonatePageData(state),
  }));
  const donateText = getActionVerb(page, urlPartner === 'f4d-nvi' ? 'contribute' : undefined);
  const donationText = getActionNoun(page, urlPartner === 'f4d-nvi' ? 'contribution' : undefined);
  const shoppingCartIds = candidates
    .filter(c => (c.donationAmount ?? 0) > 0)
    .map(c => c.recipient_id);
  const { handleShare, isPopupOpen } = useShareUrl({
    userRefCode: userState?.data?.ref_code,
    shoppingCartIds,
  });
  const isDonateAgainVisible = urlPartner !== 'mx' && !isEvent && SHOW_DONATE_AGAIN;
  const textColor = isEvent ? theme.colors.white : theme.colors.inkBlue;
  const headerBackground = isEvent ? theme.colors.oathBlue : undefined;

  const isStatesProjectPage = getIsStatesProjectRedirectPage(clientId, urlPartner);
  const hasTrackedConfirmation = useRef(false);
  const cartId = useCompleteSavedCart(!!payment, { page, paymentProcessor: 'DE' });

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (!payment || !donationAmount || hasTrackedConfirmation.current) return;
    hasTrackedConfirmation.current = true;
    trackPurchaseOnce(payment.gid, { amount: donationAmount, unit: 'dollars' });
    trackPixel('universalPixel');
    trackDECheckoutConfirmationViewed({
      paymentProcessor: 'DE',
      userId: userState?.data?.id || '',
      candidates,
      donationAmount,
      is_recurring: getIsCheckoutMonthly(),
      client_id: clientId,
      cart_id: cartId,
    });
    // Waits for both values: donationAmount is 0 until the parent's selections load, and payment
    // is absent on a direct load. The ref stops a later donationAmount change from re-sending the
    // StackAdapt and Mixpanel calls, which have no idempotency guard of their own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment?.gid, donationAmount]);

  useEffect(() => {
    if (!payment || !isStatesProjectPage || getIsDonationPending(payment)) return;
    window.location.replace(STATES_PROJECT_REDIRECT.REDIRECT_URL);
  }, [payment]);

  if (!payment) {
    window.location.href = `/donate${window.location.search}`;
    return null;
  }

  if (isStatesProjectPage && !getIsDonationPending(payment)) {
    return null;
  }

  const dateObj = payment?.created_at ? new Date(payment.created_at) : new Date();
  const date = dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const scrollToDonateAgainSection = () => {
    const element = document.getElementById(DONATE_AGAIN_SECTION_ID);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  };

  // const pilotPartners = ['mx', 'dt', 'pd'];
  // const customPartners = ['bb'];
  // const iframeSrc =
  //   'https://app.sosha.ai/toolkit/986db138-b9d4-458f-b2fa-ba46aedb5c86?embedded=true&embedHeight=632&sliderEnabled=&showArrows=&iconsColor=%23000000&postBgColor=%23FFFFFF&postTextColor=%23000000&analyticsEnabled=';
  // const customPartnersIframeSrc =
  //   'https://app.sosha.ai/toolkit/0ecb8f48-dfad-488e-91af-41eb1a132f94?embedded=true&embedHeight=632&sliderEnabled=&showArrows=&iconsColor=%23000000&postBgColor=%23FFFFFF&postTextColor=%23000000&analyticsEnabled=';

  return (
    <>
      <ConfirmationBackgroundContainer
        $backgroundColor={isEvent ? theme.colors.inkBlue : undefined}
      >
        <ConfirmationContainer>
          <ConfirmationContainerLeft>
            <Eyebrow $color={textColor}>
              {capitalizeString(donationText)}
              {isEvent ? ' / Ticket Purchase' : ''}
              {getIsDonationPending(payment) ? ' pending' : ' complete'}
            </Eyebrow>
            <Spacing $size={5} />
            <H4 $color={textColor}>
              {getIsDonationPending(payment)
                ? `Your ${donationText} is being processed`
                : `Thank you for your ${donationText}!`}
            </H4>
            <Spacing $size={5} />
            <Body $color={textColor}>
              {getIsDonationPending(payment)
                ? 'We are currently processing your payment'
                : postDonateMsg ||
                  `Keep the momentum going and invite your friends and family to ${donateText}.`}
            </Body>
            {pageImage && <ConfirmationImage alt="Donate page logo" src={pageImage} />}
            {/* {urlPartner &&
            (customPartners.includes(urlPartner) || pilotPartners.includes(urlPartner)) &&
            !getIsDonationPending(payment) ? (
              <IFrameContainer>
                <IFrame
                  id="soshaIframe"
                  src={customPartners.includes(urlPartner) ? customPartnersIframeSrc : iframeSrc}
                  allow="camera; web-share"
                />
              </IFrameContainer>
            ) : ( */}
            <ConfirmationButtonRow>
              <ButtonBrand onClick={handleShare}>Share link</ButtonBrand>
              {isDonateAgainVisible && (
                <DonateAgainJumpButton
                  onClick={scrollToDonateAgainSection}
                  $secondary={true}
                  $hasBorder={true}
                >
                  Donate again
                </DonateAgainJumpButton>
              )}
            </ConfirmationButtonRow>
            {/* )} */}
          </ConfirmationContainerLeft>
          {getIsDonationPending(payment) ? (
            <ConfirmationCardContainer>
              <ProfileCard
                title="Pending Authorization Status"
                align="center"
                smallTitle={true}
                headerBackground={headerBackground}
              >
                <ProcessingCardContent>
                  <ProcessingImage
                    src={`${
                      ENV_CONFIG().CLOUD_STORAGE.URL
                    }/static_content/misc_icons/circle_arrows.png`}
                    alt="Circle formed by two arrows"
                  />
                  <Body>We are currently processing your payment.</Body>
                  <br />
                  <Body>
                    Once received, we will send you an email confirming your {donationText}.
                  </Body>
                </ProcessingCardContent>
              </ProfileCard>
            </ConfirmationCardContainer>
          ) : (
            <ConfirmationContainerRight>
              <ConfirmationCardContainer>
                <ProfileCard title="Thank you!" align="center" headerBackground={headerBackground}>
                  <ConfirmationCardContent>
                    <Body>Contribution Summary</Body>
                    <Spacing $size={4} />
                    <Row $justifyContent="space-between">
                      <Column $gap="3px">
                        <H4>{formatNumberAsCurrency(getDonationTotalFromPayResponse(payment))}</H4>
                        <Spacing $size={2} />
                        <UtilityText>
                          {getIsCheckoutMonthly()
                            ? 'Recurring monthly contribution'
                            : 'One-time contribution'}
                        </UtilityText>
                        <UtilityText>
                          Including {formatNumberAsCurrency(getTipFromPayResponse(payment))} tip to{' '}
                          Oath
                        </UtilityText>
                      </Column>
                      <Column>
                        <H4>
                          {formatNumber(candidateCount)} recipient{candidateCount !== 1 && 's'}
                        </H4>
                      </Column>
                    </Row>
                    <Spacing $size={13} />
                    <Column $gap="3px">
                      <Body>{date}</Body>
                      <ConfirmationPaymentDisplay>
                        <Body>{getPaymentMethodDisplay(payment)}</Body>
                      </ConfirmationPaymentDisplay>
                      <Spacing $size={6} />
                      <Body>{payment.donor_full_name}</Body>
                      <Body>{payment.donor_email}</Body>
                    </Column>
                    {/* hiding until history is real-time <Spacing $size={22} />
                    <Row $around>
                      <Link $color={theme.colors.shaft} to={getPathWithRef('/profile')}>
                        <BodySmall $color={theme.colors.shaft}>View contribution history</BodySmall>
                      </Link>
                    </Row> */}
                  </ConfirmationCardContent>
                </ProfileCard>
              </ConfirmationCardContainer>
              <Spacing $size={14} />
              <Body>We’ve emailed you a receipt for your records.</Body>
            </ConfirmationContainerRight>
          )}
          <PopupModal autoClose={true} isSelected={isPopupOpen} title="Copied to Clipboard" />
        </ConfirmationContainer>
      </ConfirmationBackgroundContainer>
      {isDonateAgainVisible && (
        <DEDonateAgain
          id={DONATE_AGAIN_SECTION_ID}
          donationAmount={donationAmount}
          handleNewDonationSelections={handleNewDonationSelections}
        />
      )}
    </>
  );
};
